//#region Imports Packages and Libs

import { store } from "../../store/store";
import { MessageStructureComponent, InlineClickAction } from "./../../types";
import { ClickEventHandler } from "./handlers/click_event_handler";
import { InlineActionDispatcher } from "./handlers/inline_action_dispatcher";
import { ComponentMapper } from "./utils/component_mapper";
import { UrlUtils } from "./utils/url_utils";
import { createAndRedirectToLink } from "../action_handler";

//#endregion

//#region InlineClickAction Class

/**
 * Handles binding and unbinding of inline click actions for a container element.
 * Provides a mechanism to register and trigger custom actions on click events.
 */
export class InlineActionHandler {
	//#region Properties

	private container: HTMLElement | null;
	private components: MessageStructureComponent[];
	private clickActions: Record<string, InlineClickAction>;
	private clickEventHandler: ClickEventHandler;
  private messageId: string;
	
	//#endregion

	//#region Constructor

	/**
	 * Creates an instance of InlineActionHandler.
	 *
	 * @param components - An array of message structure components to handle.
	 * @param container - The HTML element to bind click events to.
	 * @param clickActions - A map of action names to InlineClickAction handlers.
	 */
	constructor(
		components: MessageStructureComponent[],
		container: HTMLElement | null,
		clickActions: Record<string, InlineClickAction> = {},
		messageId: string = ""
	) {
		this.components = components;
		this.container = container;
		this.clickActions = clickActions;
		this.messageId = messageId;

		// Ensure 'this' is preserved in event handler callbacks
		// This is necessary to ensure that 'this' refers to the class instance
		this.clickEventHandler = new ClickEventHandler(this.container, this.handleLinkClick.bind(this));
	}

	//#endregion

	/**
	 * Binds the click event handler to the container element.
	 * Does nothing if the container is null.
	 */
	public bind(): void {
		if (!this.clickEventHandler || typeof this.clickEventHandler.bind !== "function") return;
		this.clickEventHandler.bind();
	}

	/**
	 * Unbinds the click event handler from the container element.
	 * Does nothing if the container is null.
	 */
	public unbind(): void {
		if (!this.clickEventHandler || typeof this.clickEventHandler.unbind !== "function") return;
		this.clickEventHandler.unbind();
	}

	//#endregion

	//#region Private Methods

	/**
	 * Handles click events on anchor tags within the component.
	 * Intercepts links that match the base action URL and prevents default navigation.
	 *
	 * @param event - The mouse event triggered by a user click.
	 */
	private handleLinkClick(event: MouseEvent): void {
		try {
			// Ensure we have a valid HTML element as the event target
			const target = event.target as HTMLElement | null;
			if (!target) return;

			// Attempt to find the nearest <a> tag from the clicked element
			const link = target.closest("a");
			if (!link) return;

			// Extract the href attribute; skip if empty
			const href = link.getAttribute("href");
			if (!href || href.trim() === "") return;

			// --- Custom logic for -iframe, -inchat, -sametab, -newtab inline URLs (must come first!) ---
			const iframePattern = /^https?-iframe:\/\//i;
			const inchatPattern = /^https?-inchat:\/\//i;
			const sametabPattern = /^https?-sametab:\/\//i;
			const newtabPattern = /^https?-newtab:\/\//i;
			if (iframePattern.test(href) || inchatPattern.test(href) || sametabPattern.test(href) || newtabPattern.test(href)) {
				event.preventDefault();
				// Determine browserTarget
				let browserTarget = "newtab";
				if (iframePattern.test(href)) browserTarget = "iframe";
				else if (inchatPattern.test(href)) browserTarget = "inchat";
				else if (sametabPattern.test(href)) browserTarget = "sametab";
				// Remove the custom protocol for the actual URL
				const url = href
					.replace(/^https?-iframe:\/\//i, "https://")
					.replace(/^https?-inchat:\/\//i, "https://")
					.replace(/^https?-sametab:\/\//i, "https://")
					.replace(/^https?-newtab:\/\//i, "https://");
				// Call the same handler as button click
				createAndRedirectToLink(url, browserTarget, undefined, false, undefined);
				return;
			}

			// Normalize the href by removing protocol decorators and lowercasing it
			const targetUrl = UrlUtils.normalizeUrl(href);

			// Retrieve the base action URL from the Vuex store
			const baseActionUrl = store.state.baseInlineActionUrl || "";
			if (!baseActionUrl) return;

			// Normalize and compare URLs to determine if it's a handled action link
			const isActionUrl = targetUrl.startsWith(baseActionUrl.toLowerCase());
			if (!isActionUrl) return;

			// Prevent the default link navigation behavior
			event.preventDefault();
			//console.log(`Intercepted href: ${href}`);

			// Find the component associated with the link
			const component =
				ComponentMapper.getComponentForLink(link, this.components) ||
				ComponentMapper.createFallbackComponent(link.outerHTML);

			// Dispatch the action using InlineActionDispatcher
			component.messageId = this.messageId;
			InlineActionDispatcher.dispatch(href, component, this.clickActions);
		} catch (ex) {
			console.log("[inline_action_handler.ts] An error has occurred \n-> Inside handleLinkClick -> Error: ", ex);
		}
	}

	//#endregion
}

//#endregion
